import { useFeatureValue } from '@growthbook/growthbook-react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useSession } from '@/auth/index';
import { useAppConfigContext } from '@/context/AppConfig';
import { useAppConstantsContext } from '@/context/AppConstants';
import { phoneFraudSearch } from '@/reports/fraud/phone/phoneFraudSearch';
import { SearchFormProps } from '@/reports/index';
import type { SearchReportResponseData } from '@/reports/searchReport';
import { RecentReportsSuggestions } from '@/reports/shared/RecentReportsSuggestions/RecentReportsSuggestions';
import {
  FcraWarning,
  InputLabel,
} from '@/reports/shared/searchFormsComponents/components';
import { Button, Grid, Icons, Input, Stack, Text } from '@/theme/index';
import {
  formatPhoneInput,
  searchFormsRecyclingValidation,
  unformatPhone,
} from '@/utils/index';
import { schemaPhoneSearchForm } from '@/utils/schemaForms/schemaForms';
import { phoneSearch } from './phoneSearch';
import type { PhoneSearchParams } from './phoneSearch';

/**
 * This component renders a search form for searching by phone number. It renders an input for the user's phone number,
 * and updates the page to the phone report page after validating the phone number.
 */
export function SearchPhoneForm({
  onSuccess,
  onError,
  closeForm,
  placement = 'header',
  fraudSearch = false,
}: SearchFormProps) {
  const { session } = useSession();
  const { account } = session;
  const { logError, trackEventGA4 } = useAppConfigContext();
  const {
    links: { tosLink, fcraLink },
    data: { brandName },
  } = useAppConstantsContext();
  const phoneQuery = useMutation<
    SearchReportResponseData,
    Error,
    PhoneSearchParams
  >({
    mutationFn: fraudSearch ? phoneFraudSearch : phoneSearch,
    onSuccess: (data, variables) => {
      onSuccess &&
        onSuccess({
          permalink: data.report.permalink,
          reportType: fraudSearch ? 'phone-fraud' : 'phone',
          searchParams: `phone=${encodeURIComponent(variables.phone)}`,
        });
    },
    onError: (error) => {
      logError('Phone Search', error);
      onError && onError(error);
    },
  });

  const phoneReportPlanValue = useFeatureValue('rev-364', 'control');

  const phoneReportPlans: { [key: string]: string[] } = {
    control: [],
    emu: ['dd_plan=emu'],
    eider: ['dd_plan=eider'],
  };

  const onPhoneSearchSubmit = ({ phone }: { phone: string }) => {
    if (!searchFormsRecyclingValidation(account)) return;
    phone = unformatPhone(phone);

    trackEventGA4({
      event_name: 'report_search',
      report_type: 'phone',
      search_by: 'phone',
      search_from: `${placement}_search`,
    });
    phoneQuery.mutate({
      phone,
      report_flags: JSON.stringify(
        phoneReportPlans[phoneReportPlanValue] ?? []
      ),
    });
  };

  const {
    handleSubmit,
    formState: { errors },
    reset,
    control,
    watch,
  } = useForm<{
    phone: string;
  }>({
    resolver: yupResolver(schemaPhoneSearchForm),
    defaultValues: {
      phone: '',
    },
  });

  const phone = watch('phone');
  const [previousPhone, setPreviousPhone] = useState(phone);
  const isInHeader = placement === 'header';
  const isNotInDashboard = placement !== 'dashboard';

  return (
    <form id="search-phone" onSubmit={handleSubmit(onPhoneSearchSubmit)}>
      <Grid container spacing={2} px={2} alignItems="flex-end" flexWrap="wrap">
        <Grid item width={{ xs: '100%', md: '40%' }} order={1}>
          {isNotInDashboard && (
            <InputLabel htmlFor="phone">Phone Number</InputLabel>
          )}
          <Controller
            control={control}
            name="phone"
            render={({ field }) => (
              <Input
                {...field}
                fullWidth
                id="phone"
                type="tel"
                placeholder="(123) 456-7890"
                error={Boolean(errors.phone)}
                onChange={(e) => {
                  const input = e.target.value;
                  if (!previousPhone && input.length) {
                    field.onChange(formatPhoneInput(input));
                    setPreviousPhone(formatPhoneInput(input));
                  } else if (input.length < previousPhone.length) {
                    field.onChange(input);
                    setPreviousPhone(input);
                  } else {
                    field.onChange(formatPhoneInput(input));
                    setPreviousPhone(formatPhoneInput(input));
                  }
                }}
                inputProps={{
                  maxLength: 17,
                  'data-cy': 'phone_header_field',
                }}
              />
            )}
          />
        </Grid>
        <Grid item width={{ xs: '100%', md: '15%' }} order={{ xs: 3, md: 2 }}>
          <Button
            type="submit"
            variant="contained"
            color="success"
            fullWidth
            data-cy="phone_btn_header"
          >
            Search
          </Button>
        </Grid>
        {phone && (
          <Grid item width="100%" order={{ xs: 2, md: 3 }}>
            <Button onClick={() => reset()} color="success">
              Clear field(s)
            </Button>
          </Grid>
        )}
      </Grid>
      <Stack py={1} px={2}>
        {errors.phone && (
          <Text color="error">
            <Icons.Warning
              fontSize="small"
              sx={{ verticalAlign: 'bottom', mr: 0.5 }}
            />
            {errors.phone.message}
          </Text>
        )}
        <Text variant="body2" marginBottom={1}>
          <Icons.Lightbulb
            fontSize="small"
            color="primary"
            sx={{ verticalAlign: 'bottom', mr: 0.5 }}
          />
          Enter a phone number to search for a possible owner, associated
          address, carrier details, photos, additional contact info and much
          more
        </Text>
        {isInHeader && (
          <RecentReportsSuggestions
            reportType="reverse_phone_report"
            closeForm={closeForm}
          />
        )}
        <FcraWarning
          fcraLink={fcraLink}
          tosLink={tosLink}
          brandName={brandName}
        />
      </Stack>
    </form>
  );
}
